import React from 'react';
import { Entity } from '@backstage/catalog-model';
import { Avatar, Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  EntityRefLink,
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import {
  Box,
  Card,
  Chip,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { TeamsChatLink } from '../../TeamsChat';
import {
  ContactProfile,
  ServiceProviderFacts,
} from './useServiceProviderFacts';
import { ServiceList } from './ServiceList';
import { VuidChip, vuidOf } from '../../VuidChip';

/** The parts of `spec.contactChannels` the card shows, see ContactChannelsCard. */
type ContactChannels = {
  email?: { name: string; address: string; url: string }[];
  jiraSpaces?: { name: string; space: string; url: string }[];
};

/** A contact as the CMDB provider puts it on a team. */
type Contact = { entityRef: string; mail?: string };

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    transition: theme.transitions.create('box-shadow'),
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
    // What is a link of its own on the card sits above the link of the card.
    '& a:not($cardLink), & button': {
      position: 'relative',
      zIndex: 1,
    },
  },
  // The link to the page of the team, stretched over the whole card.
  cardLink: {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '&:focus-visible::after': {
      outline: `2px solid ${theme.palette.primary.main}`,
      borderRadius: theme.shape.borderRadius,
    },
  },
  header: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold as number,
    lineHeight: 1.3,
  },
  // The title with the vuid chip on its line, a little apart from it.
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  department: {
    display: 'block',
    color: theme.palette.text.secondary,
  },
  content: {
    padding: theme.spacing(1, 1.5, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  description: {
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  label: {
    display: 'block',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    lineHeight: 1.6,
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  people: {
    display: 'flex',
    flexWrap: 'wrap',
    // Room for the photos, which stand out over the top of their tiles - above
    // the first row and between the rows, where a photo would otherwise reach
    // into the tile above it.
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(5),
    marginTop: theme.spacing(3.5),
  },
  // One person the way the member cards show them: a framed tile with the
  // photo half over its top edge, and the name inside.
  person: {
    position: 'relative',
    width: theme.spacing(13),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    // The same height for every tile, whatever the name: two lines for it,
    // one for the company, and the chat at the bottom edge.
    minHeight: theme.spacing(15),
    padding: theme.spacing(4.5, 0.75, 0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    background: theme.palette.background.paper,
  },
  photo: {
    position: 'absolute',
    top: -theme.spacing(3.5),
    left: '50%',
    transform: 'translateX(-50%)',
  },
  personName: {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightMedium as number,
    lineHeight: 1.25,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
  },
  personCompany: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    marginTop: theme.spacing(0.25),
  },
  personChat: {
    marginTop: 'auto',
  },
}));

/** A section of the card under a small label, left out with nothing in it. */
const Section = (props: { label: string; children: React.ReactNode }) => {
  const classes = useStyles();
  return (
    <div>
      <Typography variant="caption" className={classes.label}>
        {props.label}
      </Typography>
      {props.children}
    </div>
  );
};

/**
 * A display name the way Entra has it - `Kreck, Christopher [RTL Tech]` - as a
 * tile shows it: `Christopher Kreck`, and the company on a line of its own. A
 * name in any other shape is shown as it is.
 */
export function readableName(displayName: string): {
  name: string;
  company?: string;
} {
  const match = displayName.match(/^(.*?)\s*\[(.+)\]\s*$/);
  const person = (match ? match[1] : displayName).trim();
  const company = match?.[2].trim();
  const [last, first, ...rest] = person.split(',').map(part => part.trim());
  const name =
    first && rest.length === 0 ? `${first} ${last}` : person || displayName;
  return { name, company };
}

/**
 * Who to talk to: the accountable and the main contacts of the team, each
 * with their photo the way the member cards show it and a chat in Teams. No
 * group chat or mail here: the gallery is for finding the right person, the
 * page of the team offers them.
 */
const People = (props: {
  team: Entity;
  profileOf: (userName: string) => ContactProfile | undefined;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const spec = props.team.spec ?? {};
  const accountable = spec.accountableContact as Contact | undefined;
  const main = ((spec.mainContacts as Contact[] | undefined) ?? []).filter(
    it => it.entityRef,
  );
  // Accountable first; someone who is both is shown once.
  const contacts = [
    ...(accountable?.entityRef ? [accountable] : []),
    ...main,
  ].filter(
    (contact, index, all) =>
      all.findIndex(it => it.entityRef === contact.entityRef) === index,
  );
  if (contacts.length === 0) {
    return null;
  }
  const person = (contact: Contact) => {
    const profile = props.profileOf(contact.entityRef);
    const name = readableName(profile?.displayName ?? contact.entityRef);
    return (
      <div className={classes.person} key={contact.entityRef}>
        <div className={classes.photo}>
          <Avatar
            displayName={profile?.displayName ?? contact.entityRef}
            picture={profile?.picture}
            customStyles={{
              width: 56,
              height: 56,
              fontSize: 20,
              // A ring in the colour of the card lifts the photo off the
              // edge of its tile.
              border: `3px solid ${theme.palette.background.paper}`,
              boxShadow: theme.shadows[2],
            }}
          />
        </div>
        <span className={classes.personName}>
          <EntityRefLink
            entityRef={{
              kind: 'user',
              namespace: 'default',
              name: contact.entityRef,
            }}
            defaultKind="user"
            hideIcon
          >
            {name.name}
          </EntityRefLink>
        </span>
        {name.company && (
          <span className={classes.personCompany}>{name.company}</span>
        )}
        {contact.mail && (
          <span className={classes.personChat}>
            <TeamsChatLink users={[contact.mail]} />
          </span>
        )}
      </div>
    );
  };
  return (
    <Section label="People">
      <div className={classes.people}>{contacts.map(person)}</div>
    </Section>
  );
};

/** Where the team takes requests: addresses and Jira projects. */
const ContactChannelsSection = (props: { channels?: ContactChannels }) => {
  const classes = useStyles();
  const email = props.channels?.email ?? [];
  const jiraSpaces = props.channels?.jiraSpaces ?? [];
  if (email.length === 0 && jiraSpaces.length === 0) {
    return null;
  }
  return (
    <Section label="Contact">
      <Box display="flex" flexWrap="wrap">
        {email.map(link => (
          <Chip
            key={`${link.name}-${link.address}`}
            className={classes.chip}
            size="small"
            clickable
            component="a"
            href={link.url}
            label={link.address}
          />
        ))}
        {jiraSpaces.map(link => (
          <Chip
            key={`${link.name}-${link.space}`}
            className={classes.chip}
            size="small"
            clickable
            component="a"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            label={`Jira ${link.space}`}
          />
        ))}
      </Box>
    </Section>
  );
};

/**
 * Props for {@link TeamCard}.
 *
 * @public
 */
export type TeamCardProps = {
  entity: Entity;
  /** The title of the department the team belongs to, if it has one. */
  department?: string;
  /** What the team offers, see useServiceProviderFacts. */
  facts: ServiceProviderFacts;
  /** The name and photo of a contact by user name, see useServiceProviderFacts. */
  profileOf: (userName: string) => ContactProfile | undefined;
};

/**
 * A team of the platform at a glance: what it is about, what it offers, who to talk to, and where it takes requests. Its links - the CMDB
 * file, Wiz, dashboards - are left to the page of the team.
 *
 * The whole card leads to the page of the team: the link of its title is
 * stretched over it, so that a middle click or a keyboard opens it like any
 * other link. What is a link of its own on it - a person, a link icon, an
 * address - sits above and goes where it says instead.
 *
 * @public
 */
export const TeamCard = (props: Readonly<TeamCardProps>) => {
  const { entity, department, facts, profileOf } = props;
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);
  const title = entity.metadata.title ?? entity.metadata.name;
  const vuid = vuidOf(entity);
  const teamPath = entityRoute(entityRouteParams(entity));

  return (
    <Card className={classes.card}>
      <div className={classes.header}>
        {/* Where the team sits first, the way a breadcrumb reads. */}
        {department && (
          <Typography variant="caption" className={classes.department}>
            {department}
          </Typography>
        )}
        <div className={classes.titleRow}>
          <Typography variant="h6" component="h3" className={classes.title}>
            <Link
              to={teamPath}
              color="inherit"
              underline="none"
              className={classes.cardLink}
            >
              {title}
            </Link>
          </Typography>
          {/* Next to the heading rather than in it, which names the team and
              nothing else. A button, so it sits above the link stretched over
              the card and copies the vuid rather than opening the team. */}
          {vuid && <VuidChip vuid={vuid} />}
        </div>
      </div>
      <div className={classes.content}>
        {entity.metadata.description && (
          <Typography variant="body2" className={classes.description}>
            {entity.metadata.description}
          </Typography>
        )}
        {facts.services.length > 0 && (
          <Section label="Services">
            <ServiceList services={facts.services} />
          </Section>
        )}
        <People team={entity} profileOf={profileOf} />
        <ContactChannelsSection
          channels={entity.spec?.contactChannels as ContactChannels | undefined}
        />
      </div>
    </Card>
  );
};
